import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useChatInbox, CHAT_INBOX_DEFAULT_PAGE_SIZE, CHAT_INBOX_MAX_PAGE_SIZE } from "./useChatInbox.js";
import { getChatThread, sendChatReply } from "./chatApi.js";
import "./chatInbox.css";
import "../pages/states.css";

import PageSkeleton, { ThreadSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Support", "ข้อความจากแรงงาน"];
const SEARCH_DEBOUNCE_MS = 350;

// ChatPolicy.MaxBodyLength / ChatPolicy.MaxPageSize on the backend. Kept as
// literals with the server name beside them rather than fetched, because the
// server enforces them anyway - these only exist so the operator is stopped
// before a 400 rather than after one.
const MAX_BODY_LENGTH = 2000;
const THREAD_PAGE_SIZE = 200;

// The phone polls its side every 10 seconds. The Backoffice reads the open
// conversation on the same rhythm so a worker and an Agent see a reply within
// the same few seconds of each other, and sweeps the inbox less often because
// a list of conversations changes far more slowly than one open thread.
const THREAD_POLL_MS = 10000;
const INBOX_POLL_MS = 30000;

const SUBTITLE_TEXT =
  "บทสนทนาจริงระหว่างแรงงานกับ Agent ผู้ดูแล เปิดอ่านและตอบกลับได้จากหน้านี้";

const POLICY_LINES = [
  "แก้ไขหรือลบข้อความไม่ได้ทั้งสองฝั่ง เพราะแรงงานใช้ช่องทางนี้แจ้งเรื่องค่าจ้าง การบาดเจ็บ และสภาพการทำงาน",
  "การเปิดบทสนทนาถือเป็นการอ่าน ระบบทำเครื่องหมายว่าอ่านแล้วให้เอง ไม่มีปุ่มรับทราบแยกต่างหาก",
  "Audit Log บันทึกเฉพาะความยาวข้อความ ไม่บันทึกเนื้อหาของข้อความ",
  "ขอบเขตการมองเห็นมาจาก session ของผู้ใช้เท่านั้น หน้านี้ไม่เคยส่งรหัส Agent ไปกำหนดขอบเขตให้ตัวเอง",
];

function describeInboxError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงบทสนทนาในขอบเขตนี้";
  }
  if (error?.status === 404) {
    return "ระบบข้อความยังไม่พร้อมใช้งานบนเซิร์ฟเวอร์นี้ กรุณาแจ้งผู้ดูแลระบบ";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeThreadError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์อ่านบทสนทนานี้";
  }
  if (error?.status === 404) {
    return "ไม่พบบทสนทนานี้";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "ไม่สามารถโหลดบทสนทนาได้ กรุณาลองใหม่อีกครั้ง";
}

function describeSendError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ตอบกลับบทสนทนานี้ ข้อความยังอยู่ในช่องพิมพ์";
  }
  if (error?.status === 404) {
    return "ไม่พบบทสนทนานี้แล้ว ข้อความยังไม่ถูกส่ง";
  }
  if (error?.status === 400) {
    return error?.body?.message ?? "ข้อความนี้ส่งไม่ได้ กรุณาตรวจสอบความยาวข้อความอีกครั้ง";
  }
  return "ส่งข้อความไม่สำเร็จ ข้อความยังอยู่ในช่องพิมพ์ กรุณากดส่งอีกครั้ง";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} UTC`;
}

function formatTimeTh(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDayTh(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

/** The day heading a message belongs under, or null when it continues the previous one. */
function dayBreakBefore(message, previous) {
  const day = formatDayTh(message.sentAtUtc);
  if (!previous) {
    return day;
  }
  return formatDayTh(previous.sentAtUtc) === day ? null : day;
}

function workerLabel(thread) {
  return thread?.workerName || thread?.workerCode || thread?.workerId || "-";
}

/**
 * /backoffice/messages - the Agent's half of P154 worker-to-Agent messaging.
 * The worker's half is the Flutter screen at
 * mobile-app/lib/features/agent_chat/; this is where the reply comes from.
 *
 * Entirely over the P154 Backoffice Chat API: a scope-isolated list of
 * conversations (GET .../chat/threads), one conversation and its history
 * (GET .../chat/threads/{threadId}), and a reply
 * (POST .../chat/threads/{threadId}/messages).
 *
 * This screen renders no edit control and no delete control, for the same
 * reason the backend exposes neither route: a worker writes here about pay,
 * injuries and conditions, and the party those complaints are about must not
 * be able to make them disappear. Nothing here sends a push/SMS/LINE
 * notification, and nothing here attaches a file - the API carries a text
 * body and nothing else, so an attach button would be a promise.
 *
 * Scope (SuperAdmin sees every conversation, an Agent sees only their own
 * workers') is resolved entirely server-side from the caller's session token.
 * This page never receives, constructs or forwards an Agent id to claim
 * scope.
 */
export default function ChatInboxPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(CHAT_INBOX_DEFAULT_PAGE_SIZE);

  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadState, setThreadState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [threadReloadToken, setThreadReloadToken] = useState(0);

  const [draft, setDraft] = useState("");
  const [sendState, setSendState] = useState({ status: "idle", error: null });

  const transcriptRef = useRef(null);
  // Message count of the thread as last rendered, so a poll that changed
  // nothing does not re-scroll the operator's view and does not refetch the
  // inbox. A ref, not state - this must never itself cause a render.
  const renderedCountRef = useRef({ threadId: null, count: -1 });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filters = useMemo(() => ({ search, pageSize }), [search, pageSize]);

  const { status: inboxStatus, data: inboxData, error: inboxError, refetch: refetchInbox } = useChatInbox(filters);

  const threads = useMemo(() => inboxData?.threads ?? [], [inboxData]);

  // Inbox sweep. Deliberately does not reach into the open conversation - that
  // has its own, faster timer below.
  useEffect(() => {
    const timer = setInterval(() => refetchInbox(), INBOX_POLL_MS);
    return () => clearInterval(timer);
  }, [refetchInbox]);

  // The open conversation, refetched whenever it changes, when the operator
  // asks, and on the poll timer. The whole (capped) transcript is re-read each
  // time rather than paged forward with afterSentAtUtc: the server decides the
  // order and the timestamps, and keeping a second, client-stitched idea of
  // either is how the two sides start disagreeing about the conversation.
  useEffect(() => {
    if (!selectedThreadId) {
      setThreadState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setThreadState((previous) => ({
      status: "loading",
      data: previous.forId === selectedThreadId ? previous.data : null,
      error: null,
      forId: selectedThreadId,
    }));

    getChatThread(selectedThreadId, { pageSize: THREAD_PAGE_SIZE })
      .then((data) => {
        if (!cancelled) {
          setThreadState({ status: "ready", data, error: null, forId: selectedThreadId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setThreadState((previous) => ({
            status: "error",
            data: previous.forId === selectedThreadId ? previous.data : null,
            error,
            forId: selectedThreadId,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedThreadId, threadReloadToken]);

  const reloadThread = useCallback(() => setThreadReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!selectedThreadId) {
      return undefined;
    }
    const timer = setInterval(() => reloadThread(), THREAD_POLL_MS);
    return () => clearInterval(timer);
  }, [selectedThreadId, reloadThread]);

  const thread = threadState.status === "idle" ? null : threadState.data;
  const messages = useMemo(() => thread?.messages ?? [], [thread]);

  // Opening a conversation is what marks it read, server-side, inside the GET
  // above. So whenever the transcript actually changed, the unread badges in
  // the list beside it are stale and the list is refetched - but only then,
  // never on a poll that returned the same messages.
  useEffect(() => {
    if (threadState.status !== "ready" || !threadState.forId) {
      return;
    }
    const previous = renderedCountRef.current;
    if (previous.threadId === threadState.forId && previous.count === messages.length) {
      return;
    }
    renderedCountRef.current = { threadId: threadState.forId, count: messages.length };

    const transcript = transcriptRef.current;
    if (transcript) {
      transcript.scrollTop = transcript.scrollHeight;
    }
    refetchInbox();
  }, [threadState.status, threadState.forId, messages.length, refetchInbox]);

  function openThread(threadId) {
    if (threadId === selectedThreadId) {
      return;
    }
    setSelectedThreadId(threadId);
    setDraft("");
    setSendState({ status: "idle", error: null });
  }

  function handleSend() {
    const body = draft.trim();
    if (!selectedThreadId || !body || sendState.status === "sending") {
      return;
    }

    setSendState({ status: "sending", error: null });
    sendChatReply(selectedThreadId, body)
      .then(() => {
        // The draft is only cleared once the server has the message. A reply
        // that did not land must stay in the box, where it can be sent again -
        // never silently dropped, and never left looking sent.
        setDraft("");
        setSendState({ status: "idle", error: null });
        reloadThread();
      })
      .catch((error) => {
        setSendState({ status: "error", error });
      });
  }

  const selectedInboxRow = threads.find((row) => row.threadId === selectedThreadId) ?? null;
  const totalUnread = threads.reduce((sum, row) => sum + (row.unreadCount ?? 0), 0);
  const isInitialLoading = inboxStatus === "loading" && inboxData === null;
  const isInitialError = inboxStatus === "error" && inboxData === null;
  const canLoadMore = pageSize === CHAT_INBOX_DEFAULT_PAGE_SIZE && threads.length >= CHAT_INBOX_DEFAULT_PAGE_SIZE;
  const transcriptCapped = messages.length >= THREAD_PAGE_SIZE;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดบทสนทนา..." />
      </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดบทสนทนาได้</h1>
          <p className="wx-state-body">{describeInboxError(inboxError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={refetchInbox}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-chat-page">
        <header className="wx-chat-head">
          <div className="wx-chat-title">
            <h1>ข้อความจากแรงงาน</h1>
            <p>{SUBTITLE_TEXT}</p>
          </div>
          <div className="wx-chat-head-side">
            {totalUnread > 0 && (
              <span className="wx-chat-unread-total">
                ยังไม่ได้อ่าน <b>{totalUnread}</b>
              </span>
            )}
            {primaryRole && (
              <span className="wx-chat-scope-pill">
                Scope <b>{primaryRole}</b>
              </span>
            )}
          </div>
        </header>

        <div className="wx-chat-policy-banner" role="status">
          <div className="wx-chat-policy-banner-title">
            <span className="wx-chat-policy-dot" aria-hidden="true" />
            นโยบายการสนทนากับแรงงาน
          </div>
          <ul>
            {POLICY_LINES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <div className="wx-chat-body">
          <section className="wx-panel wx-chat-inbox" aria-label="รายการบทสนทนา">
            <div className="wx-chat-inbox-head">
              <h2 className="wx-panel-title">บทสนทนา</h2>
              <span className="wx-chat-inbox-count">
                {threads.length} รายการ{inboxStatus === "loading" && inboxData !== null ? " · กำลังโหลด..." : ""}
              </span>
            </div>

            <label className="wx-chat-search">
              <span className="wx-visually-hidden">ค้นหาชื่อหรือรหัสแรงงาน</span>
              <input
                type="search"
                placeholder="ค้นหาชื่อหรือรหัสแรงงาน"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </label>

            {inboxStatus === "error" && inboxData !== null && (
              <p className="wx-chat-inline-error" role="alert">
                {describeInboxError(inboxError)}
              </p>
            )}

            {threads.length === 0 ? (
              <p className="wx-empty-list">
                {search ? "ไม่พบบทสนทนาที่ตรงกับคำค้นหา" : "ยังไม่มีบทสนทนาในขอบเขตที่มองเห็น"}
              </p>
            ) : (
              <ul className="wx-chat-thread-list">
                {threads.map((row) => (
                  <li key={row.threadId}>
                    <button
                      type="button"
                      className={`wx-chat-thread-row${row.threadId === selectedThreadId ? " wx-chat-thread-row--selected" : ""}`}
                      aria-current={row.threadId === selectedThreadId ? "true" : undefined}
                      onClick={() => openThread(row.threadId)}
                    >
                      <span className="wx-chat-avatar" aria-hidden="true">
                        {workerLabel(row).slice(0, 1).toUpperCase()}
                      </span>
                      <span className="wx-chat-thread-row-main">
                        <span className="wx-chat-thread-row-top">
                          <strong>{workerLabel(row)}</strong>
                          <span className="wx-chat-thread-row-time">{formatTimeTh(row.lastMessageAtUtc)}</span>
                        </span>
                        <span className="wx-chat-thread-row-sub">{row.workerCode ?? row.workerId}</span>
                        <span className="wx-chat-thread-row-preview">
                          {row.lastMessageSenderRole === "Agent" && <span className="wx-chat-preview-prefix">คุณ: </span>}
                          {row.lastMessagePreview ?? "ยังไม่มีข้อความ"}
                        </span>
                      </span>
                      {row.unreadCount > 0 && (
                        <span className="wx-chat-unread-badge" aria-label={`ยังไม่ได้อ่าน ${row.unreadCount} ข้อความ`}>
                          {row.unreadCount}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {canLoadMore && (
              <button
                type="button"
                className="wx-button wx-button--ghost wx-chat-load-more"
                onClick={() => setPageSize(CHAT_INBOX_MAX_PAGE_SIZE)}
              >
                แสดงเพิ่มถึง {CHAT_INBOX_MAX_PAGE_SIZE} บทสนทนา
              </button>
            )}
          </section>

          <section className="wx-panel wx-chat-thread" aria-label="บทสนทนาที่เลือก">
            {!selectedThreadId ? (
              <div className="wx-chat-thread-empty">
                <h2 className="wx-state-title">เลือกบทสนทนาทางซ้าย</h2>
                <p className="wx-state-body">เปิดบทสนทนาเพื่ออ่านข้อความจากแรงงานและตอบกลับได้จากหน้านี้</p>
              </div>
            ) : (
              <>
                <header className="wx-chat-thread-head">
                  <span className="wx-chat-avatar wx-chat-avatar--lg" aria-hidden="true">
                    {workerLabel(selectedInboxRow).slice(0, 1).toUpperCase()}
                  </span>
                  <div className="wx-chat-thread-head-main">
                    <h2>{workerLabel(selectedInboxRow)}</h2>
                    <p>
                      {selectedInboxRow?.workerCode ?? thread?.workerId ?? "-"}
                      {thread && !thread.agentId && (
                        <span className="wx-badge wx-badge--warning wx-chat-head-badge">ยังไม่มี Agent ดูแล</span>
                      )}
                    </p>
                  </div>
                  <button type="button" className="wx-button wx-button--ghost wx-chat-refresh-button" onClick={reloadThread}>
                    รีเฟรช
                  </button>
                </header>

                {threadState.status === "error" && (
                  <p className="wx-chat-inline-error wx-chat-inline-error--inset" role="alert">
                    {describeThreadError(threadState.error)}
                  </p>
                )}

                <div className="wx-chat-transcript" ref={transcriptRef}>
                  {threadState.status === "loading" && threadState.data === null && (
                    <ThreadSkeleton label="กำลังโหลดบทสนทนา..." />
                  )}

                  {transcriptCapped && (
                    <p className="wx-chat-transcript-notice" role="status">
                      แสดงได้สูงสุด {THREAD_PAGE_SIZE} ข้อความแรกของบทสนทนานี้
                    </p>
                  )}

                  {threadState.status === "ready" && messages.length === 0 && (
                    <p className="wx-empty-list">ยังไม่มีข้อความในบทสนทนานี้ พิมพ์ข้อความด้านล่างเพื่อเริ่มต้น</p>
                  )}

                  {messages.map((message, index) => {
                    const dayBreak = dayBreakBefore(message, messages[index - 1]);
                    const fromAgent = message.senderRole === "Agent";
                    return (
                      <div key={message.messageId}>
                        {dayBreak && <div className="wx-chat-day-break">{dayBreak}</div>}
                        <div className={`wx-chat-bubble-row${fromAgent ? " wx-chat-bubble-row--mine" : ""}`}>
                          <div
                            className={`wx-chat-bubble${fromAgent ? " wx-chat-bubble--mine" : ""}`}
                            title={formatDateTimeTh(message.sentAtUtc) ?? undefined}
                          >
                            <p className="wx-chat-bubble-body">{message.body}</p>
                            <span className="wx-chat-bubble-meta">
                              {formatTimeTh(message.sentAtUtc)}
                              {fromAgent && (
                                <span className="wx-chat-bubble-receipt">
                                  {message.readByWorkerAtUtc ? "อ่านแล้ว" : "ส่งแล้ว"}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="wx-chat-composer">
                  {sendState.status === "error" && (
                    <p className="wx-chat-send-error" role="alert">
                      {describeSendError(sendState.error)}
                    </p>
                  )}
                  <textarea
                    className="wx-chat-composer-field"
                    aria-label="เขียนข้อความตอบกลับ"
                    placeholder="เขียนข้อความตอบกลับ"
                    maxLength={MAX_BODY_LENGTH}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                  />
                  <div className="wx-chat-composer-actions">
                    <span className="wx-chat-composer-count">
                      {draft.length}/{MAX_BODY_LENGTH}
                    </span>
                    <button
                      type="button"
                      className="wx-button wx-button--primary wx-chat-send-button"
                      disabled={!draft.trim() || sendState.status === "sending"}
                      onClick={handleSend}
                    >
                      {sendState.status === "sending" ? "กำลังส่ง..." : "ส่งข้อความ"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        <p className="wx-chat-footnote">ข้อความที่ส่งแล้วแก้ไขหรือลบไม่ได้ และหน้านี้ไม่ส่ง SMS หรือ LINE ให้แรงงาน</p>
      </div>
    </AppShell>
  );
}
